import React, { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Waves, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DDI + DDD + número, só dígitos. Ex.: 5547999999999
const WHATSAPP_NUMBER = "554799686547";

const LeadContext = createContext(null);
export const useLead = () => useContext(LeadContext);

const formatarTelefone = (valor) => {
  let d = (valor || "").replace(/\D/g, "");

  // Se o usuário colar com +55 / 55 na frente, remove o DDI
  if (d.length > 11 && d.startsWith("55")) d = d.slice(2);

  d = d.slice(0, 11); // máximo: DDD (2) + 9 dígitos

  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`; // celular (11 dígitos)
};

// Válido: DDD (11–99) + 8 dígitos (fixo) ou 9 dígitos começando com 9 (celular)
const telefoneValido = (valor) => {
  const d = (valor || "").replace(/\D/g, "");
  if (d.length === 10) return /^[1-9][1-9]/.test(d);
  if (d.length === 11) return /^[1-9][1-9]9/.test(d);
  return false;
};

export function LeadProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("landing");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [form, setForm] = useState({ nome: "", email: "", telefone: "" });
  const [tickets, setTickets] = useState(1);

  const openLead = useCallback((src = "landing") => {
    setSource(src);
    setStatus("idle");
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.telefone) {
      toast.error("Preencha todos os campos para continuar.");
      return;
    }
    if (!telefoneValido(form.telefone)) {
      toast.error("Informe um WhatsApp válido com DDD. Ex.: (47) 99999-9999");
      return;
    }
    setStatus("loading");
    try {
      const dispositivo =
        typeof window !== "undefined" && window.innerWidth < 768 ? "Mobile" : "Desktop";
      await axios.post(`${API}/leads`, { ...form, origem: source, dispositivo });
      // Meta Pixel — Lead capture event
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
      setStatus("success");
      toast.success("Recebemos seus dados! Vamos te chamar 🤿");
    } catch (err) {
      setStatus("idle");
      toast.error("Não foi possível enviar. Tente novamente.");
    }
  };

  const abrirWhatsApp = () => {
    const texto =
      `Olá! Me chamo ${form.nome} e quero garantir ${tickets} ingresso${tickets > 1 ? "s" : ""} ` +
      `da promoção *2 mergulhos pelo preço de 1*. Pode me passar as formas de pagamento?`;

    // Meta Pixel — clique para falar no WhatsApp
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Contact");
    }

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(texto)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <LeadContext.Provider value={{ openLead }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lead-modal"
          >
            <div
              className="absolute inset-0 bg-ocean-deep/70 backdrop-blur-sm"
              onClick={close}
            />
            <motion.div
              className="relative w-full sm:max-w-md glass-light rounded-t-3xl sm:rounded-3xl overflow-hidden"
              initial={{ y: 60, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="relative cta-gradient px-7 pt-7 pb-8 text-white light-rays">
                <button
                  onClick={close}
                  className="absolute right-4 top-4 rounded-full bg-white/15 p-2 hover:bg-white/25 transition-colors"
                  data-testid="lead-modal-close"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
                <Waves className="h-7 w-7 text-ocean-light mb-3" />
                {status === "success" ? (
                  <h3 className="font-display text-2xl leading-tight sm:text-3xl">
                    Tudo certo, desconto aplicado! Escolha quantos ingressos quiser com 50% de
                    desconto e fale com a gente no WhatsApp para finalizar.
                  </h3>
                ) : (
                  <>
                    <h3 className="font-display text-2xl leading-tight sm:text-3xl">
                      Você está a um passo de viver a melhor experiência da sua vida.
                    </h3>
                    <p className="mt-2 text-sm text-white/85">
                      Preencha seus dados corretamente. Nossa equipe vai te atender no WhatsApp para fechar os ingressos e enviar a agenda virtual para você escolher a melhor data do seu mergulho.
                    </p>
                  </>
                )}
              </div>

              {status === "success" ? (
                <div className="p-7 space-y-5 text-center" data-testid="lead-success">
                  {/* Seletor de ingressos */}
                  <div className="rounded-2xl border border-ocean-mid/25 bg-white/70 p-4">
                    <span className="block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
                      Quantos ingressos você quer?
                    </span>
                    <div className="mt-3 flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => setTickets((t) => Math.max(1, t - 1))}
                        disabled={tickets <= 1}
                        className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ocean-mid/30 bg-white text-2xl font-black text-brand-navy transition-colors hover:bg-ocean-mid/10 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Diminuir"
                        data-testid="tickets-decrement"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={tickets}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (Number.isNaN(v)) return setTickets(1);
                          setTickets(Math.max(1, Math.min(99, v)));
                        }}
                        className="h-14 w-20 rounded-2xl border-2 border-ocean-mid/30 bg-white text-center font-display text-3xl font-black text-brand-navy outline-none focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/30"
                        data-testid="tickets-input"
                        aria-label="Quantidade de ingressos"
                      />
                      <button
                        type="button"
                        onClick={() => setTickets((t) => Math.min(99, t + 1))}
                        disabled={tickets >= 99}
                        className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ocean-mid/30 bg-white text-2xl font-black text-brand-navy transition-colors hover:bg-ocean-mid/10 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Aumentar"
                        data-testid="tickets-increment"
                      >
                        +
                      </button>
                    </div>
                    <p className="mt-3 text-[11px] text-muted-foreground">
                      Entre 1 e 99 ingressos por compra
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={abrirWhatsApp}
                    className="cta-sun inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 font-extrabold uppercase tracking-wide text-white shadow-lg shadow-sun/30"
                    data-testid="lead-success-whatsapp"
                  >
                    Chamar no WhatsApp para {tickets} ingresso{tickets > 1 ? "s" : ""}
                  </button>
                  <p className="text-[11px] text-muted-foreground">
                    Nossa equipe responde rapidinho e te passa Pix ou cartão em até 12x
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="p-7 space-y-4" data-testid="lead-form">
                  <Field
                    label="Seu nome"
                    testid="lead-input-nome"
                    value={form.nome}
                    onChange={(v) => setForm((f) => ({ ...f, nome: v }))}
                    placeholder="Como podemos te chamar?"
                  />
                  <Field
                    label="Seu e-mail"
                    testid="lead-input-email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                    placeholder="voce@email.com"
                  />
                  <Field
                    label="WhatsApp"
                    testid="lead-input-telefone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={15}
                    value={form.telefone}
                    onChange={(v) => setForm((f) => ({ ...f, telefone: formatarTelefone(v) }))}
                    placeholder="(47) 99999-9999"
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="cta-gradient mt-2 flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-extrabold uppercase tracking-wide text-white disabled:opacity-70"
                    data-testid="lead-submit"
                  >
                    {status === "loading" ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Enviando...</>
                    ) : (
                      <>Continuar <ArrowRight className="h-5 w-5" /></>
                    )}
                  </button>
                  <p className="text-center text-xs text-muted-foreground">
                    Seus dados estão seguros. Sem spam. 🔒
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LeadContext.Provider>
  );
}

function Field({
  label, value, onChange, placeholder, type = "text", testid,
  inputMode, autoComplete, maxLength,
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy/70">
        {label}
      </span>
      <input
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-2xl border border-ocean-mid/25 bg-white/80 px-4 py-3 text-brand-navy outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/30"
      />
    </label>
  );
}